import { useEffect, useLayoutEffect, useRef, useState, lazy, Suspense } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AOS from "aos";
import "aos/dist/aos.css";

// Core components
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import About from "./components/About";
import Footer from "./components/Footer";
import LoadingSpinner from "./components/LoadingSpinner";
import logo from "./assets/logo1.png";
import ScrollToTop from "./components/ScrollToTop";

// Lazy load non-critical components with preload hint
const preloadComponent = (importFunc) => {
  const Component = lazy(() => {
    // Start preloading
    const promise = importFunc();
    // Return the promise
    return promise;
  });
  return Component;
};

// Lazy load components with preloading
const Brandgrowvideo = preloadComponent(() => import('./components/Brandgrowvideo'));
const SignatureDishes = preloadComponent(() => import('./components/SignatureDishes'));
const SpecialOffers = preloadComponent(() => import('./components/SpecialOffers'));
const ChefIntro = preloadComponent(() => import('./components/ChefIntro'));
const GallerySection = preloadComponent(() => import('./components/GallerySection'));
const Testimonials = preloadComponent(() => import('./components/Testimonials'));
const ServicesSection = preloadComponent(() => import('./components/ServicesSection'));
const WhyChooseUs = preloadComponent(() => import('./components/WhyChooseUs'));
const NattuKoliMenu = preloadComponent(() => import('./components/NattuKoliMenu'));
const NattuMuttaiMenu = preloadComponent(() => import('./components/NattuMuttaiMenu'));
const KaadaiSpecialMenu = preloadComponent(() => import('./components/KaadaiSpecialMenu'));
const BiryaniMenu = preloadComponent(() => import('./components/BiryaniMenu'));
const FriendsFamilyBucket = preloadComponent(() => import('./components/FriendsFamilyBucket'));
const FriendsFamilyBiryaniBucket = preloadComponent(() => import('./components/FriendsFamilyBiryaniBucket'));
const SundaySpecialMenu = preloadComponent(() => import('./components/SundaySpecialMenu'));
const SweetsBeveragesMenu = preloadComponent(() => import('./components/SweetsBeveragesMenu'));
const ProcessSection = preloadComponent(() => import('./components/ProcessSection'));
const FaqSection = preloadComponent(() => import('./components/FaqSection'));

// Initialize GSAP ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

function App() {
  const heroRef = useRef(null);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 900, once: true, offset: 80, easing: "ease-out" });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(false), 1800);
    return () => clearTimeout(timer);
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-heading", { opacity: 0, y: 60, duration: 1.2, ease: "power3.out" });
      gsap.from(".hero-cta a", { opacity: 0, y: 30, duration: 0.8, stagger: 0.15, delay: 0.5 });

      gsap.utils.toArray(".gsap-reveal").forEach((element) => {
        gsap.from(element, {
          opacity: 0,
          y: 40,
          duration: 0.9,
          ease: "power2.out",
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
      });

      if (heroRef.current) {
        gsap.to(heroRef.current, {
          backgroundPosition: "50% 70%",
          ease: "none",
          scrollTrigger: {
            trigger: heroRef.current,
            start: "top top",
            scrub: true,
          },
        });
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-[#060404] text-brand-cream overflow-hidden">
      {showLoader && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
          <div className="flex flex-col items-center gap-4 animate-pulse">
            <img src={logo} alt="Pannai Veetu Biryani" className="h-20 w-20 object-contain" />
            <p className="tracking-[0.4em] text-brand-gold text-xs uppercase">loading</p>
          </div>
        </div>
      )}
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(107,15,15,0.35),_transparent_45%),_radial-gradient(circle_at_bottom,_rgba(212,166,74,0.18),_transparent_55%)]" />
      <Navbar />
      <main className="pt-24">
        <Hero sectionRef={heroRef} />
        <About />
        
        {/* Group related sections together with a single Suspense boundary */}
        <Suspense fallback={<LoadingSpinner />}>
          <div className="space-y-0">
            <Brandgrowvideo />
            <SignatureDishes />
            <SpecialOffers />
            <ChefIntro />
            <GallerySection />
            <Testimonials />
            <ServicesSection />
            <WhyChooseUs />
            <NattuKoliMenu />
            <NattuMuttaiMenu />
            <KaadaiSpecialMenu />
            <BiryaniMenu />
            <FriendsFamilyBucket />
            <FriendsFamilyBiryaniBucket />
            <SundaySpecialMenu />
            <SweetsBeveragesMenu />
            <ProcessSection />
            <FaqSection />
          </div>
        </Suspense>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}

export default App;
